/**
 * GetStartedStep Component
 *
 * Renders a single expandable step in the onboarding checklist.
 * Shows completion state and expands to reveal detailed directions.
 */

import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { cn, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@scio3/ui';
import { SafeHtml } from '@/components/shared/SafeHtml';
import type { StepState } from '../types';

interface GetStartedStepProps {
  step: StepState;
  isActive: boolean;
  onToggle: () => void;
}

export function GetStartedStep({ step, isActive, onToggle }: GetStartedStepProps) {
  const { t } = useTranslation();

  return (
    <Collapsible open={isActive} onOpenChange={onToggle}>
      <div className={cn('overflow-hidden rounded-md transition-colors', isActive && 'bg-muted')}>
        {/* Step header - always visible */}
        <CollapsibleTrigger asChild>
          <button
            data-testid={`checklist-step-${step.id}-btn`}
            type="button"
            className="flex w-full cursor-pointer items-start gap-3 p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t(step.titleKey)}
          >
            {/* Step indicator: filled green square with checkmark if completed, empty gray square if pending */}
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                step.completed ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/20',
              )}
            >
              {step.completed && <Check className="h-4 w-4" strokeWidth={3} />}
            </div>

            {/* Step title (and optional label below) */}
            <div className="min-w-0">
              <span className="block text-sm font-semibold">{t(step.titleKey)}</span>
              {!step.required && (
                <span className="block text-xs text-muted-foreground">
                  ({t('getStarted.optional')})
                </span>
              )}
            </div>

            {/* Chevron indicator */}
            <ChevronDown
              className={cn(
                'mt-1 ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                isActive && 'rotate-180',
              )}
            />
          </button>
        </CollapsibleTrigger>

        {/* Expandable directions */}
        <CollapsibleContent className="overflow-hidden data-[state=open]:animate-[collapsible-down_200ms_ease-out] data-[state=closed]:animate-[collapsible-up_200ms_ease-out]">
          <div className="pb-2 pl-11 pr-2">
            <ul className="space-y-1.5 text-xs text-foreground">
              {/* Direction groups — strike through ALL bullets only when the
                  whole step is complete (no per-group strike anymore). */}
              {step.directionGroups?.map((group) =>
                group.directions.map((direction) => (
                  <li
                    key={direction}
                    data-testid={`direction-${group.settingKey}`}
                    className="flex items-baseline gap-2"
                  >
                    <span aria-hidden className="text-base leading-none">
                      &bull;
                    </span>
                    <span className={cn(step.completed && 'line-through')}>
                      <SafeHtml html={t(direction)} />
                    </span>
                  </li>
                )),
              )}

              {/* Simple directions (share step completion) */}
              {step.directions?.map((direction) => (
                <li key={direction} className="flex items-baseline gap-2">
                  <span aria-hidden className="text-base leading-none">
                    &bull;
                  </span>
                  <span className={cn(step.completed && 'line-through')}>
                    <SafeHtml html={t(direction)} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
